/* eslint-disable max-lines */
import * as urlTemplate from 'url-template';
import { environment } from '@environment';

import { isNullOrUndefined } from '@app/util/util';
import { SearchData } from '@shared/base-search-bar/entities/search-data';
import { FlightClass } from '@shared/base-search-bar/entities/flight-class';
import { PassengerArrangement, PassengerType } from '@shared/base-search-bar/entities/passenger-arrangement';
import { FlightType } from '@search-bar/entities/flight-type';
import { FLIGHT_CLASS } from '@search-bar/constants/search-bar.constants';
import { TravelerTypes } from '@pax/entities/traveler-info';
import { getTime } from '@autos/auto-results/helpers/auto.helpers';
import { formatAutoDirections } from '@autos/auto-results/mappers/auto-search.mapper';
import { AutoPlaceParams } from '@autos/auto-results/entities/autoURLParams';
import { HotelDestinationInputType } from '@shared/base-search-bar/entities/autocomplete-destionation';
import { transformIsoDate } from '@search-bar/mappers/autos-iso-date.mapper';
import {
  RecentCarsLocationType, RecentSearchBus, RecentSearchCars, RecentSearchFlight, 
  RecentSearchHotel, RecentSearchModel, RecentSearchPackages
} from '@recent-searches/entities/recent-search';
import { PackageStep } from '@packages/packages-search-bar/constants/package-step';
import { Payload } from '@flight-results/mappers/search-payload.mapper';
import { PACKAGE_V2_DIVIDER } from '@packages/packages-search-bar/mappers/packages-url.mapper';
import { autoHomeRootRoute } from '@autos/auto-home/constants/routes';
import { productResultsRoute } from '@app/constants/app-routing.constants';

export function mapSearchData(data: SearchData, isBuses: boolean = false, isRecentSearch: boolean = false): string {
  if (data.flightType === FlightType.ONEWAY) {
    return isBuses ?
      mapForOneWayByBuses(data) :
      mapForOneWay(data, isRecentSearch);
  }
  if (data.flightType === FlightType.ROUNDTRIP) {
    return isBuses ?
      mapForRoundTripByBuses(data) :
      mapForRoundTrip(data, isRecentSearch);
  }
}

export function mapRecentFlightSearchData(data: RecentSearchFlight): string {
  const passengers = data.travellers.map<PassengerArrangement>(x => {
    return { id: x.type, quantity: x.quantity || 0 };
  });
  const arrivalDate = data.arrivaldate ? new Date(transformIsoDate(data.arrivaldate)) : null;
  const departureDate = transformIsoDate(data.departuredate);
  const search = {
    flightType: data.roundtrip ? FlightType.ROUNDTRIP : FlightType.ONEWAY,
    flightClass: data.class as FlightClass,
    departurePlace: {
      code: data.from,
      description: null,
      highlight_description: null,
      state: null,
      country: null
    },
    arrivalPlace: {
      code: data.to,
      description: null,
      highlight_description: null,
      state: null,
      country: null
    },
    pickUpPlace: null,
    dropOffPlace: null,
    pickUpTime: getTime(departureDate),
    dropOffTime: arrivalDate ? getTime(arrivalDate) : null,
    departureDate: new Date(departureDate),
    arrivalDate: arrivalDate,
    airline: 'null',
    baggageIncluded: data.baggage,
    passengerArrangement: passengers,
    departureTerminal: null,
    arrivalTerminal: null,
    destinationPlace: null,
    hotelDestinationPlace: null,
    passengerArrangementsState: null,
    guestsPerRoomState: null,
    guestsPerRoom: null,
    branchCode: null,
    salesChannel: null,
    packageStep: null,
    hourType: null,
    packageDestinationPlace: null,
    packageOriginPlace: null,
    inputTravelExtra: null,
  };
  return mapSearchData(search, false, true);
}

export function mapRecentBusSearchData(data: RecentSearchBus): string {
  const arrivalDate = data.arrivaldate ? new Date(transformIsoDate(data.arrivaldate)) : null;
  const departureDate = transformIsoDate(data.departuredate);
  const search = {
    flightType: data.roundtrip ? FlightType.ROUNDTRIP : FlightType.ONEWAY,
    departureTerminal: {
      code: data.from,
      description: null,
      highlight_description: null,
      state: null,
      country: null
    },
    arrivalTerminal: {
      code: data.to,
      description: null,
      highlight_description: null,
      state: null,
      country: null
    },
    pickUpPlace: null,
    dropOffPlace: null,
    pickUpTime: getTime(departureDate),
    dropOffTime: arrivalDate ? getTime(arrivalDate) : null,
    departureDate: new Date(departureDate),
    arrivalDate: arrivalDate,
    airline: null,
    flightClass: null,
    passengerArrangement: [{ id: PassengerType.ADULTS, quantity: data.travellersQuantity }],
    baggageIncluded: null,
    departurePlace: null,
    arrivalPlace: null,
    destinationPlace: null,
    hotelDestinationPlace: null,
    passengerArrangementsState: null,
    guestsPerRoomState: null,
    guestsPerRoom: null,
    branchCode: null,
    salesChannel: null,
    packageStep: null,
    hourType: null,
    packageDestinationPlace: null,
    packageOriginPlace: null,
    inputTravelExtra: null,
  };
  return mapSearchData(search, true, true);
}

export function mapRecentHotelSearchData(data: RecentSearchHotel): string {
  return urlTemplate
    .parse('{destination_type}/{destination_id}/{departure_date}/{arrival_date}/{currency_code}/{mapped_rooms}')
    .expand({
      destination_type: data?.isHotel ? HotelDestinationInputType.hotel : HotelDestinationInputType.location,
      destination_id: data.location_id,
      departure_date: formatDate(new Date(transformIsoDate(data.checkin))),
      arrival_date: formatDate(new Date(transformIsoDate(data.checkout))),
      currency_code: environment.countryInfo.currency_code,
      mapped_rooms: mappedRoomsByRecent(data)
    });
}

export function mapRecentAutoSearchData(search: RecentSearchCars): string {
  const { origin, destination } = formatAutoDirections({
    departureDate: transformIsoDate(search.pickup?.time),
    departurePlaceCode: search.pickup?.code,
    departureIsAirport: search.pickup?.type === RecentCarsLocationType.Code,
    arrivalPlaceCode: search.dropoff?.code,
    arrivalDate: transformIsoDate(search.dropoff?.time),
    arrivalIsAirport: search.dropoff?.type === RecentCarsLocationType.Code,
    currencyCode: environment.countryInfo.currency_code,
    pickUp: {
      placeId: search.pickup?.place_id,
      lat: search.pickup?.coordinates[0],
      lng: search.pickup?.coordinates[1],
    } as AutoPlaceParams,
    dropOff: {
      placeId: search.dropoff?.place_id,
      lat: search.dropoff?.coordinates[0],
      lng: search.dropoff?.coordinates[1]
    } as AutoPlaceParams
  });
  const dest = (origin !== destination) ? destination : origin;
  return `${autoHomeRootRoute}/${productResultsRoute}/` +
    `${origin}/${search.pickup?.time}/` +
    `${search.dropoff?.time}/${dest}`;
}

export function mapRecentAirPackageSearchData(recent: RecentSearchPackages): string {
  return urlTemplate.
    parse('{flight_class}/{departure}/{destination}/{departure_date}/'
      + '{arrival_date}/{rooms}/{baggageIncluded}/{airline}/{step}')
    .expand({
      flight_class: recent.class,
      departure: recent.from,
      // eslint-disable-next-line max-len
      destination: `${recent.destination_location_id}${PACKAGE_V2_DIVIDER}${recent.destination_location_type}${PACKAGE_V2_DIVIDER}${recent.to}`,
      departure_date: formatDate(new Date(transformIsoDate(recent.departuredate))),
      arrival_date: formatDate(new Date(transformIsoDate(recent.arrivaldate))),
      airline: 'null',
      baggageIncluded: false,
      rooms: mappedRoomsByRecent(recent),
      step: PackageStep.HOTEL_RESULTS
    });
}

function mappedRoomsByRecent(recent: RecentSearchModel): string {
  const data = recent as RecentSearchHotel | RecentSearchPackages;
  return !isNullOrUndefined(data.rooms) ? data.rooms.map(item => {
    const adults = item.guests.find(x => x.type === TravelerTypes.ADULTS).quantity;
    const childrenAges = [];
    const children = item.guests.filter(x => x.type === TravelerTypes.CHILDREN)
      .map(x => {
        childrenAges.push(x.age); return x.quantity;
      });
    const childrenQuantity = !isNullOrUndefined(children) && children.length ? children.reduce((x, value) => x + value) : 0;
    return `${adults}_${childrenQuantity}_${childrenAges.join('_')}`;
  }).join('-') : null;
}

function mapForOneWay(data: SearchData, isRecentSearch: boolean = false): string {
  return urlTemplate
    .parse('{flight_type}/{flight_class}/{departure}/{arrival}/{departure_date}' +
      '/{adults}/{children}/{infants}/{baggageIncluded}/{airline}')
    .expand({
      flight_type: 'oneway',
      flight_class: isRecentSearch ? data.flightClass : mapFlightClass(data.flightClass),
      departure: data?.departurePlace?.code,
      arrival: data?.arrivalPlace?.code,
      departure_date: formatDate(data?.departureDate),
      airline: data?.airline ? data?.airline : 'null',
      baggageIncluded: data?.baggageIncluded,
      ...mapPassengers(data?.passengerArrangement)
    });
}

function mapForRoundTrip(data: SearchData, isRecentSearch: boolean = false): string {
  return urlTemplate.
    parse('{flight_type}/{flight_class}/{departure}/{arrival}/{departure_date}/'
      + '{arrival_date}/{adults}/{children}/{infants}/{baggageIncluded}/{airline}')
    .expand({
      flight_type: 'roundtrip',
      flight_class: isRecentSearch ? data.flightClass : mapFlightClass(data.flightClass),
      departure: data.departurePlace.code,
      arrival: data.arrivalPlace.code,
      departure_date: formatDate(data.departureDate),
      arrival_date: formatDate(data.arrivalDate),
      airline: data.airline ? data.airline : 'null',
      baggageIncluded: data.baggageIncluded,
      ...mapPassengers(data.passengerArrangement)
    });
}

function mapForOneWayByBuses(data: SearchData): string {
  return urlTemplate
    .parse('{bus_type}/{departure}/{arrival}/{departure_date}/{adults}/{company}')
    .expand({
      bus_type: 'oneway',
      departure: data.departureTerminal?.code,
      arrival: data.arrivalTerminal?.code,
      departure_date: formatDate(data.departureDate),
      company: data.airline ? data.airline : 'null',
      adults: data.passengerArrangement[0].quantity
    });
}

function mapForRoundTripByBuses(data: SearchData): string {
  return urlTemplate.
    parse('{bus_type}/{departure}/{arrival}/{departure_date}/{arrival_date}/{adults}/{company}')
    .expand({
      bus_type: 'roundtrip',
      departure: data.departureTerminal?.code,
      arrival: data.arrivalTerminal?.code,
      departure_date: formatDate(data.departureDate),
      arrival_date: formatDate(data.arrivalDate),
      company: data.airline ? data.airline : 'null',
      adults: data?.passengerArrangement?.length > 0 ? data?.passengerArrangement[0]?.quantity : 'null'
    });
}

export function mapFlightClass(flightClass: FlightClass): string {
  switch (flightClass) {
    case FlightClass.EXECUTIVE:
      return FLIGHT_CLASS.BUSINESS;
    case FlightClass.CHEAPER:
      return FLIGHT_CLASS.ECONOMY;
    case FlightClass.FIRST_CLASS:
      return FLIGHT_CLASS.FIRST;
    case FlightClass.PREMIUM:
      return FLIGHT_CLASS.PREMIUMECONOMY;
    default:
      return FLIGHT_CLASS.ECONOMY;
  }
}

function mapPassengers(passengers: PassengerArrangement[]): { adults: number, children: number, infants: number } {
  const adults = passengers.find(passenger => passenger.id.toUpperCase() === PassengerType.ADULTS);
  const children = passengers.find(passenger => passenger.id.toUpperCase() === PassengerType.CHILDREN);
  const infants = passengers.find(passenger => passenger.id.toUpperCase() === PassengerType.INFANTS);
  return {
    adults: adults ? adults.quantity : 0,
    children: children ? children.quantity : 0,
    infants: infants ? infants.quantity : 0
  };
}

function formatDate(date: Date): string {
  const d = new Date(date);
  let month = `${(d.getMonth() + 1)}`;
  let day = `${d.getDate()}`;
  const year = `${d.getFullYear()}`;

  if (month.length < 2) {
    month = `0${month}`;
  }
  if (day.length < 2) {
    day = `0${day}`;
  }

  return [year, month, day].join('-');
}

export function mapUrlFlexDatesParams(urlParams?: string): Payload {
  const keys: (keyof Payload)[] = [
    'flight_type',
    'flight_class',
    'departure',
    'arrival',
    'departure_date',
    'arrival_date',
    'adults',
    'children',
    'infants',
    'baggage_included',
    'airline'
  ];
  const values = urlParams ? urlParams.split('/') : [];
  const mutablePayload = {};
  const isOneWay = values[0] === FlightType.ONEWAY.toLowerCase();
  keys.forEach((key, index) => {
    if (key === 'arrival_date' && isOneWay) {
      return;
    }

    const adjustedIndex = isOneWay && index > 5 ? index - 1 : index;
    if (values[adjustedIndex] !== undefined) {
      mutablePayload[key] = key === 'baggage_included' ? JSON.parse(values[adjustedIndex]) : values[adjustedIndex];
    }
  });
  return Object.freeze(mutablePayload) as Payload;
}
